import { type ComponentProps, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { fuzzyMatch } from '../../../common/functions/fuzzy-match';
import { useDialogContainer } from '../../../common/hooks/use-dialog-container';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  type ComboboxPresetBaseProps,
  type ComboboxRootProps,
} from './combobox';
import { buildVirtualizedHighlightHandler, VirtualizedList, type VirtualizerHandle } from './virtualized-internals';

export interface StaticComboboxProps<T>
  extends ComboboxPresetBaseProps<T>,
    Pick<ComboboxRootProps<T, false>, 'value' | 'onValueChange' | 'isItemEqualToValue' | 'disabled'>,
    Omit<ComponentProps<typeof ComboboxInput>, 'value' | 'onValueChange' | 'children' | 'disabled'> {
  /** Whether `items` is currently in-flight. When true and `items` is empty, the empty slot renders a loading message instead of `emptyMessage`. */
  isLoading?: boolean;
  /** Resolved item for the current `value` when `items` has not yet loaded. When provided, it labels the trigger before `items` arrives so the input doesn't briefly show the placeholder and then "pop" to the resolved label. Redundant when `value` is already present in `items`. */
  selectedItem?: T;
}

export const StaticCombobox = <T,>({
  items,
  value,
  selectedItem,
  onValueChange,
  getKey,
  getLabel,
  isItemEqualToValue,
  emptyMessage,
  container,
  disabled,
  isLoading = false,
  estimateSize,
  ...inputProps
}: StaticComboboxProps<T>) => {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLDivElement>(null);
  const resolvedContainer = useDialogContainer(triggerRef);
  const virtualizerRef = useRef<VirtualizerHandle | null>(null);
  const onItemHighlighted = useMemo(() => buildVirtualizedHighlightHandler(virtualizerRef), []);

  // Stash getLabel behind a ref so `filter` / `effectiveItemToStringLabel` stay referentially stable
  // even when callers inline `getLabel` (the common pattern). Base-ui has both as memo deps; an
  // unstable identity re-runs the filter over all items on every parent re-render.
  const getLabelRef = useRef(getLabel);
  getLabelRef.current = getLabel;

  const filter = useCallback((item: T, query: string) => fuzzyMatch(getLabelRef.current(item), query), []);

  // Default to key-based equality so cloned values (e.g. round-tripped through react-hook-form) still match items.
  const defaultIsItemEqualToValue = useCallback((item: T, v: T) => getKey(item) === getKey(v), [getKey]);
  const effectiveIsItemEqualToValue = isItemEqualToValue ?? defaultIsItemEqualToValue;

  // The loading copy only kicks in during the initial fetch (items is empty) so background
  // refetches and filter-to-empty results keep showing the real label / emptyMessage.
  const showLoadingCopy = isLoading && !items?.length;
  const loadingLabel = t('combobox-loading');
  const effectiveItemToStringLabel = useCallback(
    (item: T | null) => {
      if (item === null || item === undefined) {
        return '';
      }
      if (showLoadingCopy && selectedItem === undefined) {
        return loadingLabel;
      }
      return getLabelRef.current(item);
    },
    [showLoadingCopy, selectedItem, loadingLabel]
  );

  return (
    // Hosts triggerRef for useDialogContainer; ComboboxInput isn't a forwardable anchor like ComboboxChips.
    <div ref={triggerRef} className="contents">
      <Combobox
        virtualized
        items={items}
        value={selectedItem ?? value ?? null}
        onValueChange={onValueChange}
        disabled={disabled}
        itemToStringLabel={effectiveItemToStringLabel}
        isItemEqualToValue={effectiveIsItemEqualToValue}
        filter={filter}
        onItemHighlighted={onItemHighlighted}
      >
        <ComboboxInput disabled={disabled} {...inputProps} />
        <ComboboxContent container={container ?? resolvedContainer}>
          <ComboboxEmpty>{showLoadingCopy ? loadingLabel : (emptyMessage ?? t('combobox-empty'))}</ComboboxEmpty>
          <VirtualizedList
            items={items}
            getKey={getKey}
            getLabel={getLabel}
            estimateSize={estimateSize}
            isLoading={false}
            hasMore={false}
            isServerFilter={false}
            virtualizerRef={virtualizerRef}
          />
        </ComboboxContent>
      </Combobox>
    </div>
  );
};
